/*
 * =====================================================================================
 * DISCLAIMER:
 * =====================================================================================
 * This localization file is a community contribution and is not officially maintained
 * by Clerk. It has been provided by the community and may not be fully aligned
 * with the current or future states of the main application. Clerk does not guarantee
 * the accuracy, completeness, or timeliness of the translations in this file.
 * Use of this file is at your own risk and discretion.
 * =====================================================================================
 */

import type { LocalizationResource } from '@clerk/shared/types';

export const esMX: LocalizationResource = {
  locale: 'es-MX',
  apiKeys: {
    action__add: undefined,
    action__search: undefined,
    copySecret: {
      formButtonPrimary__copyAndClose: 'Copiar y cerrar',
      formHint: 'Por razones de seguridad, no podrás verlo de nuevo más tarde.',
      formTitle: 'Copia tu clave API "{{name}}" ahora',
    },
    createdAndExpirationStatus__expiresOn: undefined,
    createdAndExpirationStatus__never: undefined,
    detailsTitle__emptyRow: undefined,
    formButtonPrimary__add: undefined,
    formFieldCaption__expiration__expiresOn: undefined,
    formFieldCaption__expiration__never: undefined,
    formFieldOption__expiration__180d: undefined,
    formFieldOption__expiration__1d: undefined,
    formFieldOption__expiration__1y: undefined,
    formFieldOption__expiration__30d: undefined,
    formFieldOption__expiration__60d: undefined,
    formFieldOption__expiration__7d: undefined,
    formFieldOption__expiration__90d: undefined,
    formFieldOption__expiration__never: undefined,
    formHint: undefined,
    formTitle: undefined,
    lastUsed__days: undefined,
    lastUsed__hours: undefined,
    lastUsed__minutes: undefined,
    lastUsed__months: undefined,
    lastUsed__seconds: undefined,
    lastUsed__years: undefined,
    menuAction__revoke: undefined,
    revokeConfirmation: {
      confirmationText: undefined,
      formButtonPrimary__revoke: undefined,
      formHint: undefined,
      formTitle: undefined,
    },
  },
  backButton: 'Atrás',
  badge__activePlan: undefined,
  badge__canceledEndsAt: undefined,
  badge__currentPlan: 'Plan actual',
  badge__default: 'Por defecto',
  badge__endsAt: "Termina {{ date | shortDate('en-US') }}",
  badge__expired: 'Caducado',
  badge__freeTrial: undefined,
  badge__otherImpersonatorDevice: 'Otro dispositivo de imitación',
  badge__pastDueAt: undefined,
  badge__pastDuePlan: undefined,
  badge__primary: 'Primario',
  badge__renewsAt: undefined,
  badge__requiresAction: 'Requiere acción',
  badge__startsAt: "Empieza {{ date | shortDate('en-US') }}",
  badge__thisDevice: 'Este dispositivo',
  badge__trialEndsAt: undefined,
  badge__unverified: 'No confirmado',
  badge__upcomingPlan: 'Próximo plan',
  badge__userDevice: 'Dispositivo de usuario',
  badge__you: 'Usted',
  billing: {
    addPaymentMethod__label: undefined,
    alwaysFree: undefined,
    annually: undefined,
    availableFeatures: undefined,
    billedAnnually: 'Facturado anualmente',
    billedMonthlyOnly: undefined,
    cancelFreeTrial: 'Cancelar prueba gratuita',
    cancelFreeTrialAccessUntil:
      "Tu prueba gratuita permanecerá activa hasta el {{ date | longDate('es-MX') }}. Después de eso, perderás el acceso a las funciones de prueba y no se te cobrará.",
    cancelFreeTrialTitle: '¿Cancelar prueba gratuita del plan {{plan}}?',
    cancelSubscription: 'Cancelar suscripción',
    cancelSubscriptionAccessUntil: undefined,
    cancelSubscriptionNoCharge: undefined,
    cancelSubscriptionPastDue: undefined,
    cancelSubscriptionTitle: undefined,
    cannotSubscribeMonthly: undefined,
    cannotSubscribeUnrecoverable: undefined,
    checkout: {
      description__paymentSuccessful: undefined,
      description__subscriptionSuccessful: undefined,
      downgradeNotice: undefined,
      emailForm: {
        subtitle: undefined,
        title: undefined,
      },
      lineItems: {
        title__freeTrialEndsAt: undefined,
        title__paymentMethod: 'Método de pago',
        title__statementId: 'Número de factura',
        title__subscriptionBegins: 'La suscripción empieza',
        title__totalPaid: 'Total pagado',
      },
      pastDueNotice: undefined,
      perMonth: undefined,
      title: undefined,
      title__paymentSuccessful: '¡Pago exitoso!',
      title__subscriptionSuccessful: '¡Éxito!',
      title__trialSuccess: undefined,
      totalDueAfterTrial: undefined,
    },
    credit: undefined,
    creditRemainder: undefined,
    defaultFreePlanActive: undefined,
    free: 'Gratis',
    getStarted: 'Empezar',
    keepFreeTrial: undefined,
    keepSubscription: 'Mantener suscripción',
    manage: 'Gestionar',
    manageSubscription: 'Gestionar suscripción',
    month: 'Mes',
    monthly: undefined,
    pastDue: undefined,
    pay: undefined,
    paymentMethod: {
      applePayDescription: {
        annual: undefined,
        monthly: undefined,
      },
      dev: {
        anyNumbers: undefined,
        cardNumber: undefined,
        cvcZip: undefined,
        developmentMode: undefined,
        expirationDate: undefined,
        testCardInfo: undefined,
      },
    },
    paymentMethods__label: undefined,
    highlightedPlanBadge: undefined,
    pricingTable: {
      billingCycle: undefined,
      included: undefined,
    },
    reSubscribe: 'Re-suscribirse',
    seeAllFeatures: undefined,
    startFreeTrial: undefined,
    startFreeTrial__days: undefined,
    subscribe: undefined,
    subscriptionDetails: {
      beginsOn: undefined,
      currentBillingCycle: undefined,
      endsOn: undefined,
      firstPaymentAmount: undefined,
      firstPaymentOn: undefined,
      nextPaymentAmount: undefined,
      nextPaymentOn: undefined,
      pastDueAt: undefined,
      renewsAt: undefined,
      subscribedOn: undefined,
      title: undefined,
      trialEndsOn: undefined,
      trialStartedOn: undefined,
    },
    subtotal: undefined,
    switchPlan: 'Cambiar a este plan',
    switchToAnnual: undefined,
    switchToAnnualWithAnnualPrice: undefined,
    switchToMonthly: undefined,
    switchToMonthlyWithPrice: undefined,
    totalDue: undefined,
    totalDueToday: undefined,
    viewFeatures: undefined,
    viewPayment: undefined,
    year: undefined,
  },
  configureSSO: {
    missingManageEnterpriseConnectionsPermission: {
      subtitle: 'Contacta al administrador de tu organización para ampliar tus permisos.',
      title: 'No tienes permiso para administrar el inicio de sesión único (SSO)',
    },
    navbar: {
      title: 'Configurar inicio de sesión único (SSO)',
    },
    selectProviderStep: {
      title: 'Seleccionar proveedor',
      subtitle: 'Selecciona el proveedor para el que vas a configurar SSO.',
      saml: {
        groupLabel: 'SAML',
        okta: 'Okta Workforce',
        customSaml: 'Proveedor SAML personalizado',
      },
      warning: 'Una vez que se selecciona un proveedor no puedes cambiarlo hasta que termine la configuración',
    },
    verifyEmailDomainStep: {
      title: 'Verificar correo electrónico',
      subtitle: 'Verifica la dirección de correo electrónico en la que deseas habilitar la conexión empresarial.',
      addEmailAddress: {
        formTitle: 'Necesitamos tu correo electrónico',
        formSubtitle: 'Para empezar, necesitaremos tu dirección de correo electrónico',
        inputPlaceholder: 'name@company.com',
        inputLabel: 'Dirección de correo electrónico',
      },
      emailCode: {
        formTitle: 'Verifica tu dirección de correo electrónico',
        formSubtitle: 'Ingresa el código de verificación enviado a {{identifier}}',
        resendButton: '¿No recibiste un código? Reenviar',
        verified: {
          title: 'Recibimos tu correo electrónico',
          subtitle: 'Has verificado tu dirección de correo electrónico con el siguiente correo',
          inputLabel: 'Dirección de correo electrónico verificada',
        },
      },
      domainTaken: {
        title: 'Este dominio ({{domain}}) ya tiene una conexión SSO',
        subtitle: 'Contacta al administrador de la aplicación para obtener acceso a través de la conexión existente.',
      },
    },
  },
  createOrganization: {
    formButtonSubmit: 'Crear organización',
    invitePage: {
      formButtonReset: 'Saltar',
    },
    title: 'Crear organización',
  },
  dates: {
    lastDay: "Ayer a las {{ date | timeString('es-ES') }}",
    next6Days: "{{ date | weekday('es-ES','long') }} a las {{ date | timeString('es-ES') }}",
    nextDay: "Mañana a las {{ date | timeString('es-ES') }}",
    numeric: "{{ date | numeric('es-ES') }}",
    previous6Days: "Último {{ date | weekday('es-ES','long') }} en {{ date | timeString('es-ES') }}",
    sameDay: "Hoy a las {{ date | timeString('es-ES') }}",
  },
  dividerText: 'o',
  footerActionLink__alternativePhoneCodeProvider: undefined,
  footerActionLink__useAnotherMethod: 'Usar otro método',
  footerPageLink__help: 'Ayuda',
  footerPageLink__privacy: 'Privacidad',
  footerPageLink__terms: 'Términos',
  formButtonPrimary: 'Continuar',
  formButtonPrimary__verify: 'Verificar',
  formFieldAction__forgotPassword: 'Has olvidado tu contraseña?',
  formFieldError__matchingPasswords: 'Las contraseñas coinciden.',
  formFieldError__notMatchingPasswords: 'Las no contraseñas coinciden.',
  formFieldError__verificationLinkExpired: 'El link de verificación expiro. Por favor vuelve a solicitarlo.',
  formFieldHintText__optional: 'Opcional',
  formFieldHintText__slug:
    'Un slug es una identificación legible por humanos que debe ser única. Se utiliza a menudo en URL.',
  formFieldInputPlaceholder__apiKeyDescription: undefined,
  formFieldInputPlaceholder__apiKeyExpirationDate: undefined,
  formFieldInputPlaceholder__apiKeyName: undefined,
  formFieldInputPlaceholder__backupCode: 'Ingresa el código de respaldo',
  formFieldInputPlaceholder__confirmDeletionUserAccount: 'Eliminar cuenta',
  formFieldInputPlaceholder__emailAddress: 'Ingresa tu correo electrónico',
  formFieldInputPlaceholder__emailAddress_username: 'Ingresa el correo electrónico o nombre de usuario',
  formFieldInputPlaceholder__emailAddresses:
    'Ingrese o pegue una o más direcciones de correo electrónico, separadas por espacios o comas',
  formFieldInputPlaceholder__firstName: 'Nombre',
  formFieldInputPlaceholder__lastName: 'Apellido',
  formFieldInputPlaceholder__organizationDomain: 'ejemplo.com',
  formFieldInputPlaceholder__organizationDomainEmailAddress: 'nombre@example.com',
  formFieldInputPlaceholder__organizationName: 'Nombre de la organización',
  formFieldInputPlaceholder__organizationSlug: 'mi-organización',
  formFieldInputPlaceholder__password: 'Ingresa tu contraseña',
  formFieldInputPlaceholder__phoneNumber: 'Ingresa tu número de teléfono',
  formFieldInputPlaceholder__username: undefined,
  formFieldInput__emailAddress_format: undefined,
  formFieldLabel__apiKey: 'Clave API',
  formFieldLabel__apiKeyDescription: 'Descripción',
  formFieldLabel__apiKeyExpiration: 'Expiración',
  formFieldLabel__apiKeyName: 'Nombre de clave secreta',
  formFieldLabel__automaticInvitations: 'Activar invitaciones automaticas para este dominio',
  formFieldLabel__backupCode: 'Código de respaldo',
  formFieldLabel__confirmDeletion: 'Confirmación',
  formFieldLabel__confirmPassword: 'Confirme la contraseña',
  formFieldLabel__currentPassword: 'Contraseña actual',
  formFieldLabel__emailAddress: 'Correo electrónico',
  formFieldLabel__emailAddress_username: 'Correo electrónico o nombre de usuario',
  formFieldLabel__emailAddresses: 'Direcciones de correo',
  formFieldLabel__firstName: 'Nombre',
  formFieldLabel__lastName: 'Apellido',
  formFieldLabel__newPassword: 'Nueva contraseña',
  formFieldLabel__organizationDomain: 'Dominio',
  formFieldLabel__organizationDomainDeletePending: 'Eliminar invitaciones y sugerencias pendientes',
  formFieldLabel__organizationDomainEmailAddress: 'Verificación de correo',
  formFieldLabel__organizationDomainEmailAddressDescription:
    'Entrar una dirección de correo electrónico bajo este dominio para recibir un código y verificarlo.',
  formFieldLabel__organizationName: 'Nombre de la Organización',
  formFieldLabel__organizationSlug: 'Apodo',
  formFieldLabel__passkeyName: 'Nombre de llave de acceso',
  formFieldLabel__password: 'Contraseña',
  formFieldLabel__phoneNumber: 'Número de teléfono',
  formFieldLabel__role: 'Rol',
  formFieldLabel__signOutOfOtherSessions: 'Cerrar sesión en todos los demás dispositivos',
  formFieldLabel__username: 'Nombre de usuario',
  impersonationFab: {
    action__signOut: 'Cerrar',
    title: 'Registrado como {{identifier}}',
  },
  lastAuthenticationStrategy: 'Último uso',
  maintenanceMode: 'Actualmente estamos en mantenimiento, pero no te preocupes, no debería llevar más de unos minutos.',
  membershipRole__admin: 'Administrador',
  membershipRole__basicMember: 'Miembro',
  membershipRole__guestMember: 'Invitado',
  organizationList: {
    action__createOrganization: 'Crear organización',
    action__invitationAccept: 'Unirse',
    action__suggestionsAccept: 'Pedir unirse',
    createOrganization: 'Crear Organización',
    invitationAcceptedLabel: 'Unido',
    subtitle: 'para continuar con {{applicationName}}',
    suggestionsAcceptedLabel: 'Aprobación pendiente',
    title: 'Elige una cuenta',
    titleWithoutPersonal: 'Elige una organización',
  },
  organizationProfile: {
    apiKeysPage: {
      title: undefined,
    },
    badge__automaticInvitation: 'Invitaciones automaticas',
    badge__automaticSuggestion: 'Sugerencias automaticas',
    badge__manualInvitation: 'Sin inscripciónes automaticas',
    badge__unverified: 'No verificado',
    billingPage: {
      paymentHistorySection: {
        empty: undefined,
        notFound: undefined,
        tableHeader__amount: undefined,
        tableHeader__date: undefined,
        tableHeader__status: undefined,
      },
      paymentMethodsSection: {
        actionLabel__default: undefined,
        actionLabel__remove: undefined,
        add: undefined,
        addSubtitle: undefined,
        cancelButton: undefined,
        formButtonPrimary__add: undefined,
        formButtonPrimary__pay: undefined,
        payWithTestCardButton: undefined,
        removeMethod: {
          messageLine1: undefined,
          messageLine2: undefined,
          successMessage: undefined,
          title: undefined,
        },
        title: undefined,
      },
      start: {
        headerTitle__payments: undefined,
        headerTitle__plans: undefined,
        headerTitle__statements: undefined,
        headerTitle__subscriptions: undefined,
      },
      statementsSection: {
        empty: undefined,
        itemCaption__paidForPlan: undefined,
        itemCaption__proratedCredit: undefined,
        itemCaption__subscribedAndPaidForPlan: undefined,
        notFound: undefined,
        tableHeader__amount: undefined,
        tableHeader__date: undefined,
        title: undefined,
        totalPaid: undefined,
      },
      subscriptionsListSection: {
        actionLabel__manageSubscription: undefined,
        actionLabel__newSubscription: undefined,
        actionLabel__switchPlan: undefined,
        tableHeader__edit: undefined,
        tableHeader__plan: undefined,
        tableHeader__startDate: undefined,
        title: undefined,
      },
      subscriptionsSection: {
        actionLabel__default: undefined,
      },
      switchPlansSection: {
        title: undefined,
      },
      title: undefined,
    },
    createDomainPage: {
      subtitle:
        'Añade el dominio para verificar. Los usuarios con direcciones de correo electrónico en este dominio pueden unirse a la organización aquí o pedir unirse.',
      title: 'Anadir dominio',
    },
    invitePage: {
      detailsTitle__inviteFailed:
        'No se pudieron enviar las invitaciones. Solucione lo siguiente y vuelva a intentarlo:',
      formButtonPrimary__continue: 'Enviar invitaciones',
      selectDropdown__role: 'Seleccionar rol',
      subtitle: 'Ingresa o pega una o más direcciones de correo electrónico, separadas por espacios o comas',
      successMessage: 'Invitaciones enviadas con éxito',
      title: 'Invitar miembros',
    },
    membersPage: {
      action__invite: 'Invitar',
      action__search: 'Buscar',
      activeMembersTab: {
        menuAction__remove: 'Eliminar miembro',
        tableHeader__actions: 'Acciones',
        tableHeader__joined: 'Se unió',
        tableHeader__role: 'Rol',
        tableHeader__user: 'Usuario',
      },
      alerts: {
        roleSetMigrationInProgress: {
          subtitle:
            'Estamos actualizando los roles disponibles. Una vez hecho esto, podrás actualizar los roles de nuevo.',
          title: 'Los roles están temporalmente bloqueados',
        },
      },
      detailsTitle__emptyRow: 'No hay miembros para mostrar',
      invitationsTab: {
        autoInvitations: {
          headerSubtitle:
            'Invita usuarios conectando un correo electrónico del dominio con su organización. Cualquiera que se registre con un correo electrónico del dominio existente podrá unirse a la organización en cualquier momento.',
          headerTitle: 'Invitaciones automáticas',
          primaryButton: 'Gestionar dominios verificados',
        },
        table__emptyRow: 'No hay invitaciones para mostrar',
      },
      invitedMembersTab: {
        menuAction__revoke: 'Revocar invitación',
        tableHeader__invited: 'Invitado',
      },
      requestsTab: {
        autoSuggestions: {
          headerSubtitle:
            'Los usuarios que inicien sesión con un correo electrónico del dominio existente podrán ver una sugerencia para solicitar unirse a su organización.',
          headerTitle: 'Sugerencias automáticas',
          primaryButton: 'Gestionar dominios verificados',
        },
        menuAction__approve: 'Aprobado',
        menuAction__reject: 'Rechazado',
        tableHeader__requested: 'Acceso solicitado',
        table__emptyRow: 'No hay solicitudes para mostrar',
      },
      start: {
        headerTitle__invitations: 'Invitaciones',
        headerTitle__members: 'Miembros',
        headerTitle__requests: 'Solicitudes',
      },
    },
    navbar: {
      apiKeys: 'Claves API',
      billing: 'Facturación',
      description: 'Gestiona tu organización.',
      general: 'General',
      members: 'Miembros',
      title: 'Organización',
    },
    plansPage: {
      alerts: {
        noPermissionsToManageBilling: undefined,
      },
      title: undefined,
    },
    profilePage: {
      dangerSection: {
        deleteOrganization: {
          actionDescription: 'Escribe "{{organizationName}}" a continuación para continuar.',
          messageLine1: '¿Estás seguro que quieres eliminar esta organización?',
          messageLine2: 'Esta acción es permanente e irreversible.',
          successMessage: 'Haz eliminado la organización.',
          title: 'Eliminar la organización',
        },
        leaveOrganization: {
          actionDescription: 'Escribe "{{organizationName}}" a continuación para continuar.',
          messageLine1:
            '¿Estás seguro de que deseas abandonar esta organización? Perderás el acceso a esta organización y sus aplicaciones.',
          messageLine2: 'Esta acción es permanente e irreversible.',
          successMessage: 'Has dejado la organización.',
          title: 'Abandonar la organización',
        },
        title: 'Peligro',
      },
      domainSection: {
        menuAction__manage: 'Gestionar',
        menuAction__remove: 'Eliminar',
        menuAction__verify: 'Verificar',
        primaryButton: 'Añadir dominio',
        subtitle:
          'Permite a los usuarios conectarse automáticamente o solicitar unirse a la organización basado en un correo electrónico del dominio verificado.',
        title: 'Dominios verificados',
      },
      successMessage: 'La organización ha sido actualizada.',
      title: 'Perfil de la organización',
    },
    removeDomainPage: {
      messageLine1: 'Se eliminará el de correo electrónico del dominio {{domain}}.',
      messageLine2:
        'Los usuarios no podrán unirse a la organización de manera automática una vez que se haya eliminado.',
      successMessage: '{{domain}} se ha eliminado.',
      title: 'Eliminar dominio',
    },
    start: {
      headerTitle__general: 'General',
      headerTitle__members: 'Miembros',
      profileSection: {
        primaryButton: 'Actualizar perfil',
        title: 'Perfil de la organización',
        uploadAction__title: 'Logo',
      },
    },
    verifiedDomainPage: {
      dangerTab: {
        calloutInfoLabel: 'Eliminar este dominio afectará a los usuarios invitados.',
        removeDomainActionLabel__remove: 'Eliminar dominio',
        removeDomainSubtitle: 'Eliminar este dominio de los dominios verificados',
        removeDomainTitle: 'Eliminar dominio',
      },
      enrollmentTab: {
        automaticInvitationOption__description:
          'Los usuarios son invitados automáticamente a la organización cuando se registran y pueden unirse en cualquier momento.',
        automaticInvitationOption__label: 'Invitaciones automáticas',
        automaticSuggestionOption__description:
          'Los usuarios reciben una sugerencia para solicitar unirse, pero deben ser aprobados por un administrador antes de poder unirse a la organización.',
        automaticSuggestionOption__label: 'Sugerencias automáticas',
        calloutInfoLabel: 'Cambiar el modo de inscripción solo afectará a los nuevos usuarios.',
        calloutInvitationCountLabel: 'Invitaciones pendientes enviadas a usuarios: {{count}}',
        calloutSuggestionCountLabel: 'Sugerencias pendientes enviadas a usuarios: {{count}}',
        manualInvitationOption__description: 'Los usuarios solo pueden ser invitados manualmente a la organización.',
        manualInvitationOption__label: 'Sin inscripción automática',
        subtitle: 'Seleccione cómo los usuarios de este dominio pueden unirse a la organización.',
      },
      start: {
        headerTitle__danger: 'Peligro',
        headerTitle__enrollment: 'Opciones de inscripción',
      },
      subtitle: 'El dominio {{domain}} ahora está verificado. Continúa seleccionando el modo de inscripción.',
      title: 'Actualizar {{domain}}',
    },
    verifyDomainPage: {
      formSubtitle: 'Introduce el código de verificación enviado a tu dirección de correo electrónico',
      formTitle: 'Código de verificación',
      resendButton: 'No recibiste un código? Reenviar',
      subtitle: 'El dominio {{domainName}} necesita ser verificado vía correo electrónico.',
      subtitleVerificationCodeScreen:
        'Se envió un código de verificación a {{emailAddress}}. Introduce el código para continuar.',
      title: 'Verificar dominio',
    },
  },
  organizationSwitcher: {
    action__closeOrganizationSwitcher: undefined,
    action__createOrganization: 'Crear Organización',
    action__invitationAccept: 'Unirse',
    action__manageOrganization: 'Administrar Organización',
    action__openOrganizationSwitcher: undefined,
    action__suggestionsAccept: 'Solicitar unirse',
    notSelected: 'Ninguna organización seleccionada',
    personalWorkspace: 'Espacio personal',
    suggestionsAcceptedLabel: 'Pendiente de aprobación',
  },
  paginationButton__next: 'Siguiente',
  paginationButton__previous: 'Anterior',
  paginationRowText__displaying: 'Mostrando',
  paginationRowText__of: 'de',
  reverification: {
    alternativeMethods: {
      actionLink: 'Solicitar ayuda',
      actionText: '¿No posees ninguno de estos?',
      blockButton__backupCode: 'Utiliza un código de respaldo',
      blockButton__emailCode: 'Envía el código a {{identifier}}',
      blockButton__passkey: 'Utiliza tu llave de acceso',
      blockButton__password: 'Continúa con tu contraseña',
      blockButton__phoneCode: 'Enviar un mensaje de texto a {{identifier}}',
      blockButton__totp: 'aplicación de autenticación',
      getHelp: {
        blockButton__emailSupport: 'Soporte por correo electrónico',
        content: 'Si tienes problemas para iniciar sesión',
        title: 'Obtener ayuda',
      },
      subtitle: '¿Tienes problemas? Puedes utilizar cualquiera de estos métodos para iniciar sesión',
      title: 'Utiliza otro método',
    },
    backupCodeMfa: {
      subtitle: 'Tu código de respaldo es el que obtuviste cuando configuraste la autenticación en dos pasos',
      title: 'Ingresa tu código de respaldo',
    },
    emailCode: {
      formTitle: 'Código de verificación',
      resendButton: '¿No recibiste un código? Reenviar',
      subtitle: 'para continuar con {{applicationName}}',
      title: 'Revisa tu correo electrónico',
    },
    noAvailableMethods: {
      message: 'No se puede proceder con el inicio de sesión. No hay ningún factor de autenticación disponible',
      subtitle: 'Ocurrió un error',
      title: 'No se puede iniciar sesión',
    },
    passkey: {
      blockButton__passkey: 'Utiliza tu llave de acceso',
      subtitle:
        'Utilizar tu llave de acceso confirma que eres tú. Tu dispositivo puede solicitar tu huella dactilar, rostro o pantalla de bloqueo.',
      title: 'Utiliza tu llave de acceso',
    },
    password: {
      actionLink: 'Utiliza otro método',
      subtitle: 'Ingresa tu contraseña actual para continuar',
      title: 'Verificación requerida',
    },
    phoneCode: {
      formTitle: 'Código de verificación',
      resendButton: '¿No recibiste un código? Reenviar',
      subtitle: 'Ingresa el código enviado a tu teléfono para continuar',
      title: 'Verificación requerida',
    },
    phoneCodeMfa: {
      formTitle: 'Código de verificación',
      resendButton: '¿No recibiste un código? Reenviar',
      subtitle: 'Ingresa el código enviado a tu teléfono para continuar',
      title: 'Verificación requerida',
    },
    totpMfa: {
      formTitle: 'Código de verificación',
      subtitle: 'Ingresa el código generado por la aplicación de autenticación para continuar',
      title: 'Verificación requerida',
    },
  },
  signIn: {
    accountSwitcher: {
      action__addAccount: 'Agregar cuenta',
      action__signOutAll: 'Cerrar sesión de todas las cuentas',
      subtitle: 'Seleccione la cuenta con la que desea continuar.',
      title: 'Elija una cuenta',
    },
    alternativeMethods: {
      actionLink: 'Obtener ayuda',
      actionText: '¿No tienes ninguno de estos?',
      blockButton__backupCode: 'Usa un código de respaldo',
      blockButton__emailCode: 'Enviar código a{{identifier}}',
      blockButton__emailLink: 'Enviar enlace a{{identifier}}',
      blockButton__passkey: 'Inicia sesión con tu llave de acceso',
      blockButton__password: 'Inicia sesión con tu contraseña',
      blockButton__phoneCode: 'Enviar código a{{identifier}}',
      blockButton__totp: 'Usa tu aplicación de autenticación',
      getHelp: {
        blockButton__emailSupport: 'Soporte de correo electrónico',
        content:
          'Si tienes problemas para ingresar a tu cuenta, envíenos un correo electrónico y trabajaremos contigo para restablecer el acceso lo antes posible.',
        title: 'Obtener ayuda',
      },
      subtitle: 'Si estás experimentando problemas, puedes utilizar uno de estos métodos para ingresar.',
      title: 'Utiliza otro método',
    },
    alternativePhoneCodeProvider: {
      formTitle: undefined,
      resendButton: undefined,
      subtitle: undefined,
      title: undefined,
    },
    backupCodeMfa: {
      subtitle: 'para continuar a {{applicationName}}',
      title: 'Introduce un código de seguridad',
    },
    emailCode: {
      formTitle: 'Código de verificación',
      resendButton: 'Re-enviar código',
      subtitle: 'para continuar a {{applicationName}}',
      title: 'Revise su correo electrónico',
    },
    emailCodeMfa: {
      formTitle: 'Revise su correo electrónico',
      resendButton: '¿No recibiste un código? Reenviar',
      subtitle: 'para continuar a {{applicationName}}',
      title: 'Revise su correo electrónico',
    },
    emailLink: {
      clientMismatch: {
        subtitle:
          'Para continuar, abre el enlace de verificación en tu dispositivo o navegador donde intentaste iniciar sesión',
        title: 'El enlace de verificación es inválido para este dispositivo',
      },
      expired: {
        subtitle: 'Regresa a la pestaña original para continuar.',
        title: 'El enlace de verificación ha expirado',
      },
      failed: {
        subtitle: 'Regresa a la pestaña original para continuar.',
        title: 'El enlace de verificación es inválido',
      },
      formSubtitle: 'Utiliza el enlace de verificación enviado a tu correo electrónico',
      formTitle: 'Enlace de verificación',
      loading: {
        subtitle: 'Serás redireccionado pronto',
        title: 'Iniciando sesión...',
      },
      resendButton: '¿No recibiste un enlace? Reenviar',
      subtitle: 'para continuar con {{applicationName}}',
      title: 'Revisa tu correo electrónico',
      unusedTab: {
        title: 'Puedes cerrar esta pestaña',
      },
      verified: {
        subtitle: 'Serás redireccionado pronto',
        title: 'Sesión iniciada correctamente',
      },
      verifiedSwitchTab: {
        subtitle: 'Regresa a la pestaña original para continuar',
        subtitleNewTab: 'Regresa a la pestaña recién abierta para continuar',
        titleNewTab: 'Sesión iniciada en otra pestaña',
      },
    },
    emailLinkMfa: {
      formSubtitle: 'Utiliza el enlace de verificación enviado a tu correo electrónico',
      resendButton: '¿No recibiste el enlace? Reenviar',
      subtitle: 'para continuar a {{applicationName}}',
      title: 'Revisa tu correo electrónico',
    },
    enterpriseConnections: {
      subtitle: undefined,
      title: undefined,
    },
    forgotPassword: {
      formTitle: 'Código para restablecer contraseña',
      resendButton: '¿No recibiste un código? Reenviar',
      subtitle: 'para restablecer tu contraseña',
      subtitle_email: 'Primero, ingresa el código enviado a tu correo electrónico',
      subtitle_phone: 'Primero, ingresa el código enviado a tu teléfono',
      title: 'Restablecer contraseña',
    },
    forgotPasswordAlternativeMethods: {
      blockButton__resetPassword: 'Restablece tu contraseña',
      label__alternativeMethods: 'O, inicia sesión con otro método',
      title: '¿Olvidaste la contraseña?',
    },
    newDeviceVerificationNotice:
      'Estás iniciando sesión desde un dispositivo nuevo. Estamos pidiendo verificación para mantener tu cuenta segura.',
    noAvailableMethods: {
      message: 'No se puede continuar con el inicio de sesión. No hay ningún factor de autenticación disponible.',
      subtitle: 'Ocurrió un error',
      title: 'No puedo iniciar sesión',
    },
    passkey: {
      subtitle:
        'Usando tu llave de acceso confirmas que eres tú. Tu dispositivo puede pedirte la huella dactilar, el rostro o el bloqueo de pantalla.',
      title: 'Usa tu llave de acceso',
    },
    password: {
      actionLink: 'Utiliza otro método',
      subtitle: 'para continuar con {{applicationName}}',
      title: 'Introduzca su contraseña',
    },
    passwordCompromised: {
      title: undefined,
    },
    passwordPwned: {
      title: 'Contraseña en peligro',
    },
    passwordUntrusted: {
      title: undefined,
    },
    phoneCode: {
      formTitle: 'Código de verificación',
      resendButton: 'Reenviar código',
      subtitle: 'para continuar con {{applicationName}}',
      title: 'Revisa tu teléfono',
    },
    phoneCodeMfa: {
      formTitle: 'Código de verificación',
      resendButton: 'Reenviar código',
      subtitle: 'para continuar con {{applicationName}}',
      title: 'Revisa tu teléfono',
    },
    resetPassword: {
      formButtonPrimary: 'Restablecer contraseña',
      requiredMessage: 'Por razones de seguridad, es necesario restablecer su contraseña.',
      successMessage:
        'Tu contraseña se ha cambiado correctamente. Te estamos redirigiendo, por favor espera un momento.',
      title: 'Establecer nueva contraseña',
    },
    resetPasswordMfa: {
      detailsLabel: 'Es necesario verificar su identidad para restablecer su contraseña.',
    },
    start: {
      actionLink: 'Registrarse',
      actionLink__join_waitlist: 'Unirse a la lista de espera',
      actionLink__use_email: 'Utilizar correo electrónico',
      actionLink__use_email_username: 'Utilizar correo electrónico o nombre de usuario',
      actionLink__use_passkey: 'Utilizar llave de acceso',
      actionLink__use_phone: 'Utilizar teléfono',
      actionLink__use_username: 'Utilizar nombre de usuario',
      actionText: '¿No tiene cuenta?',
      actionText__join_waitlist: undefined,
      alternativePhoneCodeProvider: {
        actionLink: undefined,
        label: undefined,
        subtitle: undefined,
        title: undefined,
      },
      subtitle: 'para continuar con {{applicationName}}',
      subtitleCombined: undefined,
      title: 'Iniciar sesión',
      titleCombined: 'Para continuar con {{applicationName}}',
    },
    totpMfa: {
      formTitle: 'Código de verificación',
      subtitle: 'Para continuar, por favor introduce el código generado por tu aplicación de autenticación',
      title: 'Verificación de dos pasos',
    },
    web3Solana: {
      subtitle: 'Selecciona una billetera abajo para iniciar sesión',
      title: 'Iniciar sesión con Solana',
    },
  },
  signInEnterPasswordTitle: 'Ingresa tu contraseña',
  signUp: {
    alternativePhoneCodeProvider: {
      resendButton: undefined,
      subtitle: undefined,
      title: undefined,
    },
    continue: {
      actionLink: 'Entrar',
      actionText: '¿Tienes una cuenta?',
      subtitle: 'Por favor completa los datos para continuar',
      title: 'Completa los datos faltantes',
    },
    emailCode: {
      formSubtitle: 'Ingresa el código de verificación enviado a tu correo electrónico',
      formTitle: 'Código de verificación',
      resendButton: 'Reenviar código',
      subtitle: 'Ingresa el código de verificación enviado a tu correo electrónico',
      title: 'Verifica tu correo electrónico',
    },
    emailLink: {
      clientMismatch: {
        subtitle:
          'Para continuar, abre el enlace de verificación en el dispositivo o navegador desde el que estás intentando registrarte',
        title: 'El enlace de verificación es inválido para este dispositivo',
      },
      formSubtitle: 'Utiliza el enlace de verificación enviado a tu dirección de correo electrónico',
      formTitle: 'Enlace de verificación',
      loading: {
        title: 'Registrando...',
      },
      resendButton: '¿No recibiste el enlace? Reenviar',
      subtitle: 'para continuar a {{applicationName}}',
      title: 'Verifica tu correo electrónico',
      verified: {
        title: 'Registrado con éxito',
      },
      verifiedSwitchTab: {
        subtitle: 'Regresa a la pestaña recién abierta para continuar',
        subtitleNewTab: 'Regresa a la pestaña anterior para continuar',
        title: 'Correo electrónico verificado con éxito',
      },
    },
    enterpriseConnections: {
      subtitle: undefined,
      title: undefined,
    },
    legalConsent: {
      checkbox: {
        label__onlyPrivacyPolicy: undefined,
        label__onlyTermsOfService: undefined,
        label__termsOfServiceAndPrivacyPolicy: undefined,
      },
      continue: {
        subtitle: undefined,
        title: undefined,
      },
    },
    phoneCode: {
      formSubtitle: 'Introduzca el código de verificación enviado a su número de teléfono.',
      formTitle: 'Código de verificación',
      resendButton: 'Reenviar código',
      subtitle: 'para continuar a {{applicationName}}',
      title: 'Verifica tu teléfono',
    },
    restrictedAccess: {
      actionLink: 'Iniciar sesión',
      actionText: '¿Tienes cuenta?',
      blockButton__emailSupport: 'Soporte por correo electrónico',
      blockButton__joinWaitlist: 'Unirse a la lista de espera',
      subtitle: 'Los registros están desactivados actualmente. Si crees que tienes acceso, contacta soporte',
      subtitleWaitlist:
        'Los registros están desactivados actualmente. Para ser el primero en saber cuando estemos listos, únete a la lista de espera',
      title: 'Acceso restringido',
    },
    start: {
      actionLink: 'Inicar sesión',
      actionLink__use_email: 'Utilizar el correo electrónico',
      actionLink__use_phone: 'Utilizar el número telefónico',
      actionText: '¿Tienes una cuenta?',
      alternativePhoneCodeProvider: {
        actionLink: undefined,
        label: undefined,
        subtitle: undefined,
        title: undefined,
      },
      subtitle: 'para continuar con {{applicationName}}',
      subtitleCombined: 'para continuar con {{applicationName}}',
      title: 'Crea tu cuenta',
      titleCombined: 'Crea tu cuenta',
    },
    web3Solana: {
      subtitle: 'Selecciona una billetera abajo para registrarte',
      title: 'Registrarse con Solana',
    },
  },
  socialButtonsBlockButton: 'Continuar con {{provider|titleize}}',
  socialButtonsBlockButtonManyInView: '{{provider|titleize}}',
  taskChooseOrganization: {
    alerts: {
      organizationAlreadyExists:
        'Ya existe una organización para el nombre de empresa detectado ({{organizationName}}) y {{organizationDomain}}. Únete por invitación.',
    },
    chooseOrganization: {
      action__createOrganization: 'Crear nueva organización',
      action__invitationAccept: 'Unirse',
      action__suggestionsAccept: 'Pedir unirse',
      subtitle: 'Unirse a una organización existente o crear una nueva',
      subtitle__createOrganizationDisabled: 'Unirse a una organización existente',
      suggestionsAcceptedLabel: 'Pendiente de aprobación',
      title: 'Elegir una organización',
    },
    createOrganization: {
      formButtonReset: 'Cancelar',
      formButtonSubmit: 'Continuar',
      formFieldInputPlaceholder__name: 'Mi Organización',
      formFieldInputPlaceholder__slug: 'mi-organizacion',
      formFieldLabel__name: 'Nombre',
      formFieldLabel__slug: 'Identificador',
      subtitle: 'Ingrese los detalles de su organización para continuar',
      title: 'Configurar su organización',
    },
    organizationCreationDisabled: {
      subtitle: 'Contacte al administrador de su organización para obtener una invitación.',
      title: 'Debe pertenecer a una organización',
    },
    signOut: {
      actionLink: 'Cerrar sesión',
      actionText: 'Registrado como {{identifier}}',
    },
  },
  taskResetPassword: {
    formButtonPrimary: undefined,
    signOut: {
      actionLink: undefined,
      actionText: undefined,
    },
    subtitle: undefined,
    title: undefined,
  },
  taskSetupMfa: {
    badge: undefined,
    signOut: {
      actionLink: undefined,
      actionText: undefined,
    },
    smsCode: {
      addPhone: {
        formButtonPrimary: undefined,
        infoText: undefined,
      },
      addPhoneNumber: undefined,
      cancel: undefined,
      subtitle: undefined,
      success: {
        finishButton: undefined,
        message1: undefined,
        message2: undefined,
        title: undefined,
      },
      title: undefined,
      verifyPhone: {
        formButtonPrimary: undefined,
        formTitle: undefined,
        resendButton: undefined,
        subtitle: undefined,
        title: undefined,
      },
    },
    start: {
      methodSelection: {
        phoneCode: undefined,
        totp: undefined,
      },
      subtitle: undefined,
      title: undefined,
    },
    totpCode: {
      addAuthenticatorApp: {
        buttonAbleToScan__nonPrimary: undefined,
        buttonUnableToScan__nonPrimary: undefined,
        formButtonPrimary: undefined,
        formButtonReset: undefined,
        infoText__ableToScan: undefined,
        infoText__unableToScan: undefined,
        inputLabel__unableToScan1: undefined,
      },
      success: {
        finishButton: undefined,
        message1: undefined,
        message2: undefined,
        title: undefined,
      },
      title: undefined,
      verifyTotp: {
        formButtonPrimary: undefined,
        formButtonReset: undefined,
        formTitle: undefined,
        subtitle: undefined,
        title: undefined,
      },
    },
  },
  unstable__errors: {
    already_a_member_in_organization: '{{email}} ya es miembro de la organización.',
    avatar_file_size_exceeded:
      'El tamaño del archivo supera el límite máximo de 10 MB. Por favor, elige un archivo más pequeño.',
    avatar_file_type_invalid: 'Tipo de archivo no compatible. Por favor, sube una imagen JPG, PNG, GIF o WEBP.',
    captcha_invalid:
      'El registro falló debido a fallos en la validación de seguridad. Por favor, recarga la página o contáctanos para obtener más asistencia.',
    captcha_unavailable:
      'El registro falló debido a fallos en la validación de robot. Por favor, recarga la página o contáctanos para obtener más asistencia.',
    form_code_incorrect: 'Código incorrecto.',
    form_email_address_blocked:
      'Los servicios de correo electrónico temporal no están soportados. Por favor, use su dirección de correo electrónico regular para crear una cuenta.',
    form_identifier_exists__email_address: 'El correo electrónico ya existe',
    form_identifier_exists__phone_number: 'El número telefónico ya existe.',
    form_identifier_exists__username: 'El nombre de usuario ya existe.',
    form_identifier_not_found: 'No se encontró una cuenta con esos detalles.',
    form_new_password_matches_current: undefined,
    form_param_format_invalid: 'Formato inválido.',
    form_param_format_invalid__email_address: 'El correo electrónico debe ser válido.',
    form_param_format_invalid__phone_number: 'El número telefónico debe ser en un formato válido internacional.',
    form_param_max_length_exceeded__first_name: 'El nombre debe tener menos de 256 caracteres.',
    form_param_max_length_exceeded__last_name: 'El apellido debe tener menos de 256 caracteres.',
    form_param_max_length_exceeded__name: 'El nombre debe tener menos de 256 caracteres.',
    form_param_nil: 'Este campo es rquerido y no puede estar vacío.',
    form_param_type_invalid: undefined,
    form_param_type_invalid__email_address: undefined,
    form_param_type_invalid__phone_number: undefined,
    form_param_value_invalid: 'Valor inválido',
    form_password_compromised__sign_in: undefined,
    form_password_incorrect: 'Contraseña incorrecta.',
    form_password_length_too_short: 'La contraseña es muy corta.',
    form_password_not_strong_enough: 'La contraseña no es suficientemente segura.',
    form_password_or_identifier_incorrect:
      'La contraseña o la dirección de correo electrónico es incorrecta. Inténtalo de nuevo o usa otro método.',
    form_password_pwned:
      'Esta contraseña se encontró como parte de una brecha y no se puede utilizar, intenta con otra contraseña.',
    form_password_pwned__sign_in:
      'Esta contraseña se encontró como parte de una brecha y no se puede utilizar, por favor restablece tu contraseña.',
    form_password_size_in_bytes_exceeded:
      'La contraseña excede el número máximo de bytes permitidos. Por favor, elimine algunos caracteres especiales o reduzca la longitud de la contraseña.',
    form_password_untrusted__sign_in: undefined,
    form_password_validation_failed: 'Contraseña incorrecta',
    form_username_invalid_character: 'El nombre de usuario contiene caracteres inválidos.',
    form_username_invalid_length: 'La longitud del nombre de usuario es demasiado corta.',
    form_username_needs_non_number_char: 'Tu nombre de usuario debe contener al menos un carácter no numérico.',
    identification_deletion_failed: 'No se puede eliminar la última identificación.',
    not_allowed_access: 'No tienes permiso para acceder a esta página.',
    organization_domain_blocked: 'Este correo electrónico del dominio está bloqueado, intenta con otro.',
    organization_domain_common: 'Este correo electrónico del dominio es muy común, intenta con otro.',
    organization_domain_exists_for_enterprise_connection: 'Este dominio ya esta en uso para tú organización',
    organization_membership_quota_exceeded:
      'Alcanzaste el limite de miembros en la organización, incluyendo las invitaciones enviadas.',
    organization_minimum_permissions_needed: 'Debe existir al menos un miembro en la organización.',
    organization_not_found_or_unauthorized: undefined,
    organization_not_found_or_unauthorized_with_create_organization_disabled: undefined,
    passkey_already_exists: 'Ya se ha registrado una llave de acceso en este dispositivo.',
    passkey_not_supported: 'Las llaves de acceso no son compatibles con este dispositivo.',
    passkey_pa_not_supported:
      'El registro requiere un autenticador de plataforma, pero el dispositivo no es compatible.',
    passkey_registration_cancelled: 'El registro de la llave de acceso se ha cancelado o ha expirado.',
    passkey_retrieval_cancelled: 'La verificación de la llave de acceso se ha cancelado o ha expirado.',
    passwordComplexity: {
      maximumLength: 'menos de {{length}} caracteres',
      minimumLength: '{{length}} o más caracteres',
      requireLowercase: 'al menos una letra minúscula',
      requireNumbers: 'al menos un número',
      requireSpecialCharacter: 'al menos un carácter especial',
      requireUppercase: 'al menos una letra mayúscula',
      sentencePrefix: 'Tu contraseña debe contener',
    },
    phone_number_exists: 'Este número de telefónico ya está en uso.',
    session_exists: 'Ya has iniciado sesión',
    web3_missing_identifier: 'No se puede encontrar la extension de la billetera Web3. Instala una para continuar',
    web3_signature_request_rejected: 'Has rechazado la solicitud de firma. Inténtalo de nuevo para continuar.',
    web3_solana_signature_generation_failed:
      'Se produjo un error al generar la firma. Inténtalo de nuevo para continuar.',
    zxcvbn: {
      couldBeStronger: 'Tu contraseña funciona, pero puede ser más segura. Prueba añadiendo más caracteres.',
      goodPassword: 'Tu contraseña cumple con todos los requisitos necesarios.',
      notEnough: 'Tu contraseña no es lo suficientemente segura.',
      suggestions: {
        allUppercase: 'Escribe algunas letras en mayúsculas, pero no todas.',
        anotherWord: 'Añade palabras menos comunes.',
        associatedYears: 'Evita años asociados contigo.',
        capitalization: 'Escribe algunas letras en mayúsculas además de la primera.',
        dates: 'Evita fechas asociadas contigo.',
        l33t: "Evita sustituciones predecibles como '@' por 'a'",
        longerKeyboardPattern: 'Usa patrones de teclado más largos y cambia la dirección de escritura varias veces.',
        noNeed: 'Puedes crear contraseñas seguras sin usar símbolos, números o mayúsculas.',
        pwned: 'Si utilizas esta contraseña en otro lugar, deberías cambiarla.',
        recentYears: 'Evita años recientes.',
        repeated: 'Evita palabras y letras repetidas.',
        reverseWords: 'Evita palabras comunes escritas al revés',
        sequences: 'Evita secuencias de letras comunes.',
        useWords: 'Utiliza varias palabras, pero evita frases comunes.',
      },
      warnings: {
        common: 'Es una contraseña utilizada comúnmente.',
        commonNames: 'Nombres y apellidos comunes son fáciles de adivinar.',
        dates: 'Las fechas son fáciles de adivinar.',
        extendedRepeat: 'Patrones repetidos como "abcabcabc" son fáciles de adivinar.',
        keyPattern: 'Patrones cortos son fáciles de adivinar.',
        namesByThemselves: 'Nombres y apellidos son fáciles de adivinar.',
        pwned: 'Tu contraseña fue expuesta por una violación de datos en Internet.',
        recentYears: 'Los años recientes son fáciles de adivinar.',
        sequences: 'Patrones comunes como "abc" son fáciles de adivinar',
        similarToCommon: 'Es similar a una contraseña utilizada habitualmente.',
        simpleRepeat: 'Caracteres repetidos como "aaa" son fáciles de adivinar',
        straightRow: 'Teclas consecutivas en tu teclado son fáciles de adivinar.',
        topHundred: 'Es una contraseña utilizada con mucha frecuencia.',
        topTen: 'Es de las contraseñas más utilizadas.',
        userInputs: 'No debería haber datos personales o relacionados con esta página.',
        wordByItself: 'Palabras únicas son fáciles de adivinar.',
      },
    },
  },
  userButton: {
    action__addAccount: 'Añadir cuenta',
    action__closeUserMenu: undefined,
    action__manageAccount: 'Administrar cuenta',
    action__openUserMenu: undefined,
    action__signOut: 'Cerrar sesión',
    action__signOutAll: 'Salir de todas las cuentas',
  },
  userProfile: {
    apiKeysPage: {
      title: undefined,
    },
    backupCodePage: {
      actionLabel__copied: 'Copiado!',
      actionLabel__copy: 'Copiar todo',
      actionLabel__download: 'Descargar .txt',
      actionLabel__print: 'Imprimir',
      infoText1: 'Se habilitarán códigos de respaldo para esta cuenta.',
      infoText2:
        'Mantén los códigos de respaldo en secreto y guárdalos de forma segura. Puedes regenerar códigos de respaldo si sospechas que se han visto comprometidos.',
      subtitle__codelist: 'Guárdalos de forma segura y mantenlos en secreto.',
      successMessage:
        'Los códigos de respaldo ahora están habilitados. Puedes utilizar uno de estos para iniciar sesión en tu cuenta, si pierdes el acceso a tu dispositivo de autenticación. Cada código solo se puede utilizar una vez.',
      successSubtitle:
        'Puedes utilizar uno de estos para iniciar sesión en tu cuenta, si pierdes el acceso a tu dispositivo de autenticación.',
      title: 'Agregar verificación de código de respaldo',
      title__codelist: 'Códigos de respaldo',
    },
    billingPage: {
      paymentHistorySection: {
        empty: undefined,
        notFound: undefined,
        tableHeader__amount: undefined,
        tableHeader__date: undefined,
        tableHeader__status: undefined,
      },
      paymentMethodsSection: {
        actionLabel__default: 'Predeterminado',
        actionLabel__remove: 'Eliminar',
        add: 'Agregar un nuevo método de pago',
        addSubtitle: 'Agregar un nuevo método de pago a tu cuenta.',
        cancelButton: 'Cancelar',
        formButtonPrimary__add: 'Agregar Método de Pago',
        formButtonPrimary__pay: 'Pagar {{amount}}',
        payWithTestCardButton: undefined,
        removeMethod: {
          messageLine1: '{{identifier}} será eliminado de esta cuenta.',
          messageLine2:
            'No podrás utilizar este método de pago para ninguna suscripción actual y estas ya no funcionarán.',
          successMessage: '{{paymentMethod}} ha sido eliminada de tu cuenta.',
          title: 'Eliminar método de pago',
        },
        title: 'Opciones disponibles',
      },
      start: {
        headerTitle__payments: undefined,
        headerTitle__plans: 'Planes',
        headerTitle__statements: 'Facturas',
        headerTitle__subscriptions: 'Suscripciones',
      },
      statementsSection: {
        empty: undefined,
        itemCaption__paidForPlan: undefined,
        itemCaption__proratedCredit: undefined,
        itemCaption__subscribedAndPaidForPlan: undefined,
        notFound: undefined,
        tableHeader__amount: undefined,
        tableHeader__date: undefined,
        title: undefined,
        totalPaid: undefined,
      },
      subscriptionsListSection: {
        actionLabel__manageSubscription: undefined,
        actionLabel__newSubscription: undefined,
        actionLabel__switchPlan: undefined,
        tableHeader__edit: undefined,
        tableHeader__plan: undefined,
        tableHeader__startDate: undefined,
        title: undefined,
      },
      subscriptionsSection: {
        actionLabel__default: undefined,
      },
      switchPlansSection: {
        title: undefined,
      },
      title: 'Facturación y Pagos',
    },
    connectedAccountPage: {
      formHint: 'Selecciona un proveedor para conectar su cuenta.',
      formHint__noAccounts: 'No hay proveedores de cuentas externas disponibles.',
      removeResource: {
        messageLine1: '{{identifier}} será eliminado de esta cuenta.',
        messageLine2: 'Ya no podrás utilizar esta cuenta activa y las funciones dependientes ya no funcionarán.',
        successMessage: '{{connectedAccount}} ha sido eliminado de tu cuenta.',
        title: 'Eliminar cuenta conectada',
      },
      socialButtonsBlockButton: '{{provider|titleize}}',
      successMessage: 'El proveedor ha sido agregado a tu cuenta',
      title: 'Agregar cuenta conectada',
    },
    deletePage: {
      actionDescription: 'Escribe "Eliminar cuenta" a continuación para continuar',
      confirm: 'Eliminar cuenta',
      messageLine1:
        '¿Está seguro de que desea eliminar su cuenta? Es posible que se conserven algunos datos asociados. Para solicitar la eliminación completa de datos, comuníquese con el soporte.',
      messageLine2: 'Esta acción es permanente e irreversible.',
      title: 'Eliminar cuenta',
    },
    emailAddressPage: {
      emailCode: {
        formHint:
          'A esta dirección de correo electrónico se le enviará un correo electrónico con un código de verificación.',
        formSubtitle: 'Introduce el código de verificación enviado a {{identifier}}',
        formTitle: 'Código de verificación',
        resendButton: 'Reenviar código',
        successMessage: 'El correo electrónico {{identifier}} se ha agregado a tu cuenta.',
      },
      emailLink: {
        formHint:
          'Se enviará un correo electrónico con un enlace de verificación a esta dirección de correo electrónico.',
        formSubtitle: 'Haz clic en el enlace de verificación en el correo electrónico enviado a {{identifier}}',
        formTitle: 'Enlace de verificación',
        resendButton: 'Reenviar enlace',
        successMessage: 'El correo electrónico {{identifier}} se ha agregado a su cuenta.',
      },
      enterpriseSSOLink: {
        formButton: 'Click para iniciar sesión',
        formSubtitle: 'Completar el inicio de sesión con {{identifier}}',
      },
      formHint: 'Deberás verificar el correo electrónico antes de ser añadido a tu cuenta',
      removeResource: {
        messageLine1: '{{identifier}} será eliminado de esta cuenta.',
        messageLine2: 'Ya no podrás iniciar sesión con esta dirección de correo electrónico.',
        successMessage: '{{emailAddress}} ha sido eliminado de tu cuenta.',
        title: 'Eliminar dirección de correo electrónico',
      },
      title: 'Agregar dirección de correo electrónico',
      verifyTitle: 'Verificar dirección de correo electrónico',
    },
    formButtonPrimary__add: 'Agregar',
    formButtonPrimary__continue: 'Continuar',
    formButtonPrimary__finish: 'Terminar',
    formButtonPrimary__remove: 'Eliminar',
    formButtonPrimary__save: 'Guardar',
    formButtonReset: 'Cancelar',
    mfaPage: {
      formHint: 'Seleccione un método para agregar.',
      title: 'Agregar verificación en dos pasos',
    },
    mfaPhoneCodePage: {
      backButton: 'Usar número existente',
      primaryButton__addPhoneNumber: 'Agregar número de teléfono',
      removeResource: {
        messageLine1: '{{identifier}} dejará de recibir códigos de verificación al iniciar sesión.',
        messageLine2: 'Es posible que tu cuenta no sea tan segura. ¿Estás seguro de que quieres continuar?',
        successMessage: 'Se eliminó la verificación de dos pasos por SMS para {{mfaPhoneCode}}',
        title: 'Eliminar la verificación en dos pasos',
      },
      subtitle__availablePhoneNumbers:
        'Selecciona un número de teléfono para registrar la verificación en dos pasos por SMS.',
      subtitle__unavailablePhoneNumbers:
        'No hay números de teléfono disponibles para registrar la verificación en dos pasos por SMS.',
      successMessage1:
        'Al iniciar sesión, se te solicitará un código de verificación enviado a este número de teléfono como un paso adicional.',
      successMessage2:
        'Guarda estos códigos de respaldo y almacénalos en un lugar seguro. Si pierdes el acceso a tu dispositivo de autenticación, puedes utilizar los códigos de respaldo para iniciar sesión.',
      successTitle: 'Verificación por SMS habilitada',
      title: 'Agregar verificación por SMS',
    },
    mfaTOTPPage: {
      authenticatorApp: {
        buttonAbleToScan__nonPrimary: 'Escanea el código QR en su lugar',
        buttonUnableToScan__nonPrimary: '¿No puedes escanear el código QR?',
        infoText__ableToScan:
          'Configura un nuevo método de inicio de sesión en tu aplicación de autenticación y escanea el siguiente código QR para vincularlo a tu cuenta.',
        infoText__unableToScan:
          'Configura un nuevo método de inicio de sesión en tu autenticador e ingresa la clave que se proporciona a continuación.',
        inputLabel__unableToScan1:
          'Asegúrate de que las contraseñas de Un-Solo-Uso o Por-Tiempo estén habilitadas, luego finaliza la vinculación con tu cuenta.',
        inputLabel__unableToScan2:
          'Alternativamente, si tu autenticador admite URIs TOTP, también puedes copiar la URI completa.',
      },
      removeResource: {
        messageLine1: 'El código de verificación de este autenticador ya no será necesario al iniciar sesión.',
        messageLine2: 'Es posible que tu cuenta no sea tan segura. ¿Estás seguro de que quieres continuar?',
        successMessage: 'Se eliminó la verificación en dos pasos a través de la aplicación de autenticación.',
        title: 'Eliminar la verificación en dos pasos',
      },
      successMessage:
        'La verificación en dos pasos ahora está habilitada. Al iniciar sesión, deberás ingresar un código de verificación de este autenticador como un paso adicional.',
      title: 'Agregar aplicación de autenticación',
      verifySubtitle: 'Ingresa el código de verificación generado por tu autenticador',
      verifyTitle: 'Código de verificación',
    },
    mobileButton__menu: 'Menú',
    navbar: {
      account: 'Perfil',
      apiKeys: 'Claves API',
      billing: 'Facturación',
      description: 'Administra la información de tu cuenta.',
      security: 'Seguridad',
      title: 'Cuenta',
    },
    passkeyScreen: {
      removeResource: {
        messageLine1: '{{name}} será eliminada de esta cuenta.',
        title: 'Eliminar llave de acceso',
      },
      subtitle__rename: 'Puedes cambiar el nombre de la llave de acceso para que sea más fácil de encontrar.',
      title__rename: 'Renombrar llave de acceso',
    },
    passwordPage: {
      checkboxInfoText__signOutOfOtherSessions:
        'Se recomienda cerrar la sesión de todos los otros dispositivos que hayan utilizado su antigua contraseña.',
      readonly:
        'Tu contraseña no se puede editar actualmente porque solo se puede acceder a través de la conexión de empresa.',
      successMessage__set: 'Se ha establecido tu contraseña.',
      successMessage__signOutOfOtherSessions: 'Se cerró la sesión de todos los demás dispositivos.',
      successMessage__update: 'Tu contraseña ha sido actualizada.',
      title__set: 'Configurar contraseña',
      title__update: 'Cambiar contraseña',
    },
    phoneNumberPage: {
      infoText: 'Se enviará un mensaje de texto con un enlace de verificación a este número de teléfono.',
      removeResource: {
        messageLine1: '{{identifier}} será eliminado de esta cuenta.',
        messageLine2: 'Ya no podrás iniciar sesión con este número de teléfono.',
        successMessage: '{{phoneNumber}} ha sido eliminado de tu cuenta.',
        title: 'Eliminar número de teléfono',
      },
      successMessage: '{{identifier}} ha sido añadido a tu cuenta.',
      title: 'Agregar el número de teléfono',
      verifySubtitle: 'Ingresa el código de verificación enviado a {{identifier}}',
      verifyTitle: 'Verificar número de teléfono',
    },
    plansPage: {
      title: undefined,
    },
    profilePage: {
      fileDropAreaHint: 'Carga una imagen JPG, PNG, GIF o WEBP de menos de 10 MB',
      imageFormDestructiveActionSubtitle: 'Eliminar la imagen',
      imageFormSubtitle: 'Cargar imagen',
      imageFormTitle: 'Imagen de perfil',
      readonly: 'Tu información de perfil ha sido proporcionada por la conexión de empresa y no se puede editar.',
      successMessage: 'Tu perfil ha sido actualizado.',
      title: 'Actualizar perfil',
    },
    start: {
      activeDevicesSection: {
        destructiveAction: 'Cerrar sesión en el dispositivo',
        title: 'Dispositivos activos',
      },
      connectedAccountsSection: {
        actionLabel__connectionFailed: 'Inténtelo nuevamente',
        actionLabel__reauthorize: 'Autorizar ahora',
        destructiveActionTitle: 'Quitar',
        primaryButton: 'Conectar cuenta',
        subtitle__disconnected: 'Esta cuenta ha sido desconectada.',
        subtitle__reauthorize:
          'Los permisos requeridos han sido actualizados, y podrías experimentar limitaciones. Por favor, autoriza de nuevo esta aplicación para evitar cualquier problema',
        title: 'Cuentas conectadas',
      },
      dangerSection: {
        deleteAccountButton: 'Eliminar cuenta',
        title: 'Eliminar cuenta',
      },
      emailAddressesSection: {
        destructiveAction: 'Eliminar correo electrónico',
        detailsAction__nonPrimary: 'Establecer como primario',
        detailsAction__primary: 'Completar verificación',
        detailsAction__unverified: 'Completar verificación',
        primaryButton: 'Agregar correo electrónico',
        title: 'Correos electrónicos',
      },
      enterpriseAccountsSection: {
        primaryButton: 'Conectar cuenta',
        title: 'Cuentas de empresa',
      },
      headerTitle__account: 'Detalles de la cuenta',
      headerTitle__security: 'Seguridad',
      mfaSection: {
        backupCodes: {
          actionLabel__regenerate: 'Regenerar',
          headerTitle: 'Códigos de respaldo',
          subtitle__regenerate:
            'Obtén un nuevo conjunto de códigos de respaldo seguros. Los códigos de respaldo anteriores se eliminarán y no podrán ser utilizados.',
          title__regenerate: 'Regenerar códigos de respaldo',
        },
        phoneCode: {
          actionLabel__setDefault: 'Establecer como predeterminado',
          destructiveActionLabel: 'Eliminar número telefónico',
        },
        primaryButton: 'Añadir verificación de dos pasos',
        title: 'Verificación de dos pasos',
        totp: {
          destructiveActionTitle: 'Eliminar',
          headerTitle: 'Aplicación de autenticación',
        },
      },
      passkeysSection: {
        menuAction__destructive: 'Eliminar',
        menuAction__rename: 'Renombrar',
        primaryButton: 'Añadir llave de acceso',
        title: 'Llaves de acceso',
      },
      passwordSection: {
        primaryButton__setPassword: 'Establecer contraseña ',
        primaryButton__updatePassword: 'Cambiar contraseña',
        title: 'Contraseña',
      },
      phoneNumbersSection: {
        destructiveAction: 'Eliminar número telefónico',
        detailsAction__nonPrimary: 'Establecer como primario',
        detailsAction__primary: 'Completar la verificación',
        detailsAction__unverified: 'Completar la verificación',
        primaryButton: 'Agregar un número telefónico',
        title: 'Números telefónicos',
      },
      profileSection: {
        primaryButton: 'Actualizar perfil',
        title: 'Perfil',
      },
      usernameSection: {
        primaryButton__setUsername: 'Definir nombre de usuario',
        primaryButton__updateUsername: 'Cambiar nombre de usuario',
        title: 'Nombre de usuario',
      },
      web3WalletsSection: {
        destructiveAction: 'Quitar billetera',
        detailsAction__nonPrimary: 'Establecer como primaria',
        primaryButton: 'Conectar billetera',
        title: 'Billeteras Web3',
        web3SelectSolanaWalletScreen: {
          subtitle: 'Selecciona una billetera de Solana para conectarla a tu cuenta.',
          title: 'Agregar una billetera de Solana',
        },
      },
    },
    usernamePage: {
      successMessage: 'Tu nombre de usuario ha sido actualizado.',
      title__set: 'Actualizar nombre de usuario',
      title__update: 'Actualizar nombre de usuario',
    },
    web3WalletPage: {
      removeResource: {
        messageLine1: '{{identifier}} será eliminado de esta cuenta.',
        messageLine2: 'Ya no podrás iniciar sesión con esta billetera web3.',
        successMessage: '{{web3Wallet}} ha sido eliminado de tu cuenta.',
        title: 'Eliminar la billetera web3',
      },
      subtitle__availableWallets: 'Selecciona una billetera web3 para conectarse a tu cuenta.',
      subtitle__unavailableWallets: 'No hay billeteras web3 disponibles.',
      successMessage: 'La billetera ha sido agregada a tu cuenta.',
      title: 'Añadir billetera web3',
      web3WalletButtonsBlockButton: '{{provider|titleize}}',
    },
  },
  waitlist: {
    start: {
      actionLink: 'Iniciar sesión',
      actionText: '¿Tienes una cuenta?',
      formButton: 'Únete a la lista de espera',
      subtitle: 'Ingresa tu correo electrónico y te avisaremos cuando tú lugar esté listo',
      title: 'Únete a la lista de espera',
    },
    success: {
      message: 'Serás redirigido pronto...',
      subtitle: 'Nos pondremos en contacto contigo cuando tu lugar esté listo',
      title: '¡Gracias por unirte a la lista de espera!',
    },
  },
  web3SolanaWalletButtons: {
    connect: 'Conectar con {{walletName}}',
    continue: 'Continuar con {{walletName}}',
    noneAvailable:
      'No se detectaron billeteras Web3 de Solana. Instala una {{ solanaWalletsLink || link("wallet extension") }} compatible con Web3.',
  },
} as const;
